import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { site } from '../mock';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-[#1F3864] mb-1.5">
      {label}{required && <span className="text-red-600 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

const inputCls = 'w-full border border-slate-300 rounded-sm px-3.5 py-2.5 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/50 focus:border-[#2E75B6] transition-colors';

const Contact = () => {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', organization: '', practiceType: '', endpoints: '', message: '', hp: ''
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete the required fields.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization: form.organization,
        practice_type: form.practiceType,
        endpoints: form.endpoints,
        message: form.message,
        hp: form.hp,
      });
      setSent(true);
      toast({ title: 'Inquiry received.', description: 'We will respond within one business day.' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'We could not send your inquiry.',
        description: typeof detail === 'string'
          ? detail
          : `Please email ${site.email} or call ${site.phone}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-in">
      <section className="hero-gradient border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-[12px] uppercase tracking-[0.18em] text-[#1F3864] mb-4">Contact</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-[#1F3864] max-w-3xl">Every engagement begins with a complimentary consultation.</h1>
          <p className="mt-6 text-[17px] leading-relaxed text-slate-700 max-w-2xl">
            Tell us about your practice, your regulatory environment, and your current security posture. We will respond within one business day.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-3">
            {sent ? (
              <div className="border border-[#1F3864]/20 bg-white p-10 rounded-sm text-center">
                <CheckCircle2 size={40} className="mx-auto text-[#1F3864] mb-4" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl text-[#1F3864]">Inquiry received.</h3>
                <p className="mt-3 text-slate-700">Thank you. We will respond within one business day. For urgent matters, please call <a href={site.phoneHref}>{site.phone}</a>.</p>
                <button onClick={() => setSent(false)} className="mt-6 text-[14px] text-[#2E75B6] hover:text-[#1F3864] font-medium">Submit another inquiry →</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-sm p-6 md:p-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Full Name" required>
                    <input required className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" className={inputCls} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Phone">
                    <input className={inputCls} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </Field>
                  <Field label="Organization">
                    <input className={inputCls} value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Practice Type">
                    <select className={inputCls} value={form.practiceType} onChange={e => setForm({ ...form, practiceType: e.target.value })}>
                      <option value="">Select —</option>
                      <option>Healthcare (HIPAA-covered)</option>
                      <option>Tax & Accounting (FTC Safeguards)</option>
                      <option>Financial Advisor / Insurance</option>
                      <option>Nonprofit Organization</option>
                      <option>Small Business / Cyber-Insurance Applicant</option>
                      <option>Other</option>
                    </select>
                  </Field>
                  <Field label="Approximate Endpoints">
                    <select className={inputCls} value={form.endpoints} onChange={e => setForm({ ...form, endpoints: e.target.value })}>
                      <option value="">Select —</option>
                      <option>1–10</option>
                      <option>11–25</option>
                      <option>26–50</option>
                      <option>51–100</option>
                      <option>More than 100</option>
                    </select>
                  </Field>
                </div>
                <Field label="How can we help?" required>
                  <textarea required rows={5} className={inputCls} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Briefly describe your practice, regulatory environment, and what you would like to discuss." />
                </Field>
                {/* honeypot */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" value={form.hp} onChange={e => setForm({ ...form, hp: e.target.value })} />
                <div className="pt-2">
                  <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#1F3864] text-white text-[15px] font-medium px-6 py-3 rounded-sm hover:bg-[#17294a] transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Send Inquiry</>}
                  </button>
                  <p className="mt-3 text-[12px] text-slate-500">We do not share, sell, or resell any information submitted through this form.</p>
                </div>
              </form>
            )}
          </div>

          <aside className="md:col-span-2">
            <div className="bg-[#1F3864] text-white p-8 rounded-sm">
              <div className="text-[12px] uppercase tracking-[0.18em] text-white/70 mb-5">Direct Contact</div>
              <ul className="space-y-5">
                <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-white/70" /><span>{site.address}</span></li>
                <li className="flex items-start gap-3"><Mail size={18} className="mt-0.5 text-white/70" /><a href={`mailto:${site.email}`} className="text-white hover:underline">{site.email}</a></li>
                <li className="flex items-start gap-3"><Phone size={18} className="mt-0.5 text-white/70" /><a href={site.phoneHref} className="text-white hover:underline">{site.phone}</a></li>
              </ul>
            </div>
            <div className="mt-6 p-6 border border-slate-200 rounded-sm">
              <div className="font-serif-display text-lg text-[#1F3864] mb-2">Response Time</div>
              <p className="text-[14.5px] text-slate-600">All inquiries are read and answered by Andrew Garlick within one business day. For urgent security incidents in progress, please call directly.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
